import { useState, useEffect } from "react";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { CreditBundle } from "app/models/scheduling/CreditBundleModel";
import {
  getPackageCreditsByLessonRate,
  PackageCredit,
} from "app/utils/creditBundles";

export interface PackageCreditsState {
  credits: Map<number, PackageCredit>;
  /**
   * True while the athlete's credit bundles are still being fetched. Always
   * false when disabled (e.g. logged-out viewers), so callers can gate the
   * booking options on this without delaying anyone who has no credits.
   */
  loading: boolean;
}

/**
 * Fetches an athlete's credit bundles and maps them to lesson rate ids, so
 * booking options can indicate an applicable package credit. Exposes a loading
 * flag so the options and their package footer can render together instead of
 * the footer popping in after the bundles resolve.
 *
 * CN-1323 (CFTB-3318): when a family head is booking for a managed dependent,
 * `studentId` (the selected family member's external id) scopes the lookup to
 * that member's packages — authorized by head_of? on the BE. Omitted/null
 * fetches the caller's own bundles, so self-booking is unchanged.
 */
export default function usePackageCredits(
  enabled: boolean,
  studentId?: string | null
): PackageCreditsState {
  const [credits, setCredits] = useState<Map<number, PackageCredit>>(
    new Map()
  );
  const [loading, setLoading] = useState<boolean>(enabled);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    schedulingFetchGet(
      "/credit_bundles",
      studentId ? { student_id: studentId } : {}
    )
      .then((response: CreditBundle[]) => {
        if (!cancelled && Array.isArray(response)) {
          setCredits(getPackageCreditsByLessonRate(response));
        }
      })
      .catch(() => {
        // Booking works without the indicator — fail silently
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, studentId]);

  return { credits, loading };
}
